import type { MouseEvent } from "react";
import Link from "next/link";
import { Download } from "lucide-react";
import ImageWithFallback from "@/components/ImageWithFallback";
import { getSpotFileUrl } from "@/utils/generic";
import { Button } from "@/components/shadcn/button";
import { NoxusTooltip } from "@/components/UI/NoxusTooltip";
import { cn } from "@/lib/utils";
import { formatSizeInBytes } from "@/utils/generic";
import { getFileImage, getFileType } from "@/utils/mimetypes";
import { DataType, type DataType as DataTypeValue } from "@/generated";

const DATA_TYPE_VALUES = new Set<string>(Object.values(DataType));

export type IoFileLike = {
  uri?: string;
  url?: string;
  name: string;
  content_type?: string;
  size?: number;
};

function parseIoFileSize(size: unknown): number | undefined {
  if (typeof size === "number" && Number.isFinite(size)) return size;
  if (typeof size === "string") {
    const parsed = Number(size);
    if (Number.isFinite(parsed)) return parsed;
  }
  return undefined;
}

function parseIoFileRecord(record: Record<string, unknown>): IoFileLike | null {
  const uri = record.uri ?? record.url;
  if (typeof uri !== "string" || typeof record.name !== "string") return null;
  return {
    uri,
    name: record.name,
    content_type:
      typeof record.content_type === "string" ? record.content_type : undefined,
    size: parseIoFileSize(record.size),
  };
}

export function isIoFileLike(value: unknown): value is IoFileLike {
  if (!value || typeof value !== "object" || Array.isArray(value)) return false;
  return parseIoFileRecord(value as Record<string, unknown>) !== null;
}

/**
 * Coerce a run-result value to a file for chip/thumbnail rendering.
 *
 * By default only actual objects (or `{value: <object>}` wrappers) coerce.
 * STRING values are NOT parsed: a string output whose text happens to
 * describe a file (e.g. a KB document's metadata JSON with uri+name) must
 * render as the string it is — sniffing it made the same declared-String
 * list render some items as chips and others as text. Pass
 * `parseStrings: true` only when the output's DECLARED type is File/Image,
 * where a stringified file value genuinely is a file.
 */
export function coerceIoFileLike(
  value: unknown,
  opts?: { parseStrings?: boolean }
): IoFileLike | null {
  if (isIoFileLike(value)) {
    return parseIoFileRecord(value as Record<string, unknown>);
  }

  if (typeof value === "string" && opts?.parseStrings) {
    const trimmed = value.trim();
    if (!trimmed) return null;

    try {
      const parsed: unknown = JSON.parse(trimmed);
      if (isIoFileLike(parsed)) return parsed;
    } catch {
      // not JSON — try extracting from python-ish repr below
    }

    const uriMatch = trimmed.match(
      /['"]?(?:uri|url)['"]?\s*[:=]\s*['"]([^'"]+)['"]/
    );
    const nameMatch = trimmed.match(/['"]?name['"]?\s*[:=]\s*['"]([^'"]+)['"]/);
    if (uriMatch && nameMatch) {
      return { uri: uriMatch[1], name: nameMatch[1] };
    }
  }

  if (value && typeof value === "object" && !Array.isArray(value)) {
    const record = value as Record<string, unknown>;
    const nested = record.value;
    if (nested !== undefined && nested !== record) {
      return coerceIoFileLike(nested, opts);
    }
  }

  return null;
}

/** True when a declared IO/container type means "this is a file output". */
export function isIoFileType(type: string | undefined): boolean {
  return (
    type === "File" || type === "Image" || type === "file" || type === "image"
  );
}

export type IoTextContainer = {
  text: string;
  has_preview: boolean;
  file: IoFileLike | null;
};

/**
 * V2 returns plain text for text outputs, EXCEPT when the output is too large
 * to inline — then the backend wraps it as `{text: <preview>, has_preview,
 * file}` so the UI can show a preview and download the full content. Detect
 * that exact shape (text + has_preview) so it isn't dumped as raw JSON.
 */
export function coerceIoTextContainer(value: unknown): IoTextContainer | null {
  if (!value || typeof value !== "object" || Array.isArray(value)) return null;
  const record = value as Record<string, unknown>;
  if (
    typeof record.text !== "string" ||
    typeof record.has_preview !== "boolean"
  ) {
    return null;
  }
  return {
    text: record.text,
    has_preview: record.has_preview,
    file: isIoFileLike(record.file) ? record.file : null,
  };
}

export function resolveIoFileUri(value: IoFileLike): string | null {
  const uri = value.uri ?? value.url;
  if (!uri) return null;
  return uri;
}

export function getIoFileHref(value: IoFileLike, preview = false): string {
  const uri = resolveIoFileUri(value);
  if (!uri) return "";
  if (uri.startsWith("http://") || uri.startsWith("https://")) {
    return preview ? `${uri}${uri.includes("?") ? "&" : "?"}preview=true` : uri;
  }
  return getSpotFileUrl(uri, preview);
}

export function IoFileLink({
  value,
  className,
}: {
  value: IoFileLike;
  className?: string;
}) {
  const href = getIoFileHref(value, true);
  if (!href) return null;
  return (
    <Link href={href} target="_blank" rel="noopener noreferrer">
      <p
        className={
          className ?? "text-sm text-primary hover:underline break-words"
        }
      >
        {value.name}
      </p>
    </Link>
  );
}

function buildIoFileMeta(value: IoFileLike): string {
  const typeLabel = getFileType(value.name);
  if (value.size !== undefined) {
    return `${typeLabel} · ${formatSizeInBytes(value.size)}`;
  }
  return typeLabel;
}

export function IoFileRow({
  value,
  className,
  showDownload = true,
  compact = false,
}: {
  value: IoFileLike;
  className?: string;
  showDownload?: boolean;
  compact?: boolean;
}) {
  const previewHref = getIoFileHref(value, true);
  const downloadHref = getIoFileHref(value, false);
  const meta = buildIoFileMeta(value);
  const stopPropagation = (event: MouseEvent) => {
    event.stopPropagation();
  };

  return (
    <div
      className={cn(
        "flex w-full min-w-0 items-center rounded-md border border-input bg-transparent shadow-sm",
        compact ? "h-8 gap-2 px-2" : "h-10 gap-2.5 px-2.5",
        className
      )}
      onClick={stopPropagation}
    >
      <div className={cn("relative shrink-0", compact ? "size-3.5" : "size-5")}>
        <ImageWithFallback
          src={getFileImage(value.name)}
          alt={value.name}
          fill
        />
      </div>
      {previewHref ? (
        <Link
          href={previewHref}
          target="_blank"
          rel="noopener noreferrer"
          className={cn(
            "min-w-0 flex-1 truncate text-foreground hover:underline",
            compact ? "text-[10px]" : "text-sm"
          )}
          title={value.name}
          onClick={stopPropagation}
        >
          {value.name}
        </Link>
      ) : (
        <span
          className={cn(
            "min-w-0 flex-1 truncate text-foreground",
            compact ? "text-[10px]" : "text-sm"
          )}
          title={value.name}
        >
          {value.name}
        </span>
      )}
      <span
        className={cn(
          "shrink-0 text-muted-foreground",
          compact ? "text-[10px]" : "text-xs"
        )}
      >
        {meta}
      </span>
      {showDownload && downloadHref ? (
        <NoxusTooltip content="Download file" zIndex={1400} asChild>
          <Link
            href={downloadHref}
            download
            target="_blank"
            rel="noopener noreferrer"
            onClick={stopPropagation}
          >
            <Button
              variant="ghost"
              aria-label="Download file"
              size="iconsm"
              className={cn("shrink-0", compact && "size-6")}
            >
              <Download
                size={compact ? "0.75rem" : "1rem"}
                strokeWidth="1.25px"
              />
            </Button>
          </Link>
        </NoxusTooltip>
      ) : null}
    </div>
  );
}

export type IoTruncationMeta = {
  total_size?: number;
  truncated?: boolean;
  preview_file?: IoFileLike | null;
  list_offset?: number;
};

export type NormalizedIoContainer = IoTruncationMeta & {
  type?: string;
  value?: unknown;
};

function ioTruncationMeta(record: Record<string, unknown>): IoTruncationMeta {
  const meta: IoTruncationMeta = {};
  if (typeof record.total_size === "number")
    meta.total_size = record.total_size;
  if (record.truncated === true) meta.truncated = true;
  if (typeof record.list_offset === "number" && record.list_offset > 0)
    meta.list_offset = record.list_offset;
  if (isIoFileLike(record.preview_file))
    meta.preview_file = record.preview_file;
  return meta;
}

export function normalizeIoContainer(entry: unknown): NormalizedIoContainer {
  if (entry === null || entry === undefined) return { value: entry };
  if (typeof entry !== "object" || Array.isArray(entry)) {
    return { value: entry };
  }

  const record = entry as Record<string, unknown>;
  if ("value" in record || "type" in record) {
    const type = typeof record.type === "string" ? record.type : undefined;
    const meta = ioTruncationMeta(record);
    const file =
      type === "str"
        ? null
        : coerceIoFileLike(record.value, { parseStrings: isIoFileType(type) });
    if (file) {
      return { type: "File", value: file, ...meta };
    }
    return { type, value: record.value, ...meta };
  }

  const file = coerceIoFileLike(record);
  if (file) {
    return { type: "File", value: file };
  }

  return { value: entry };
}

export function normalizeIoContainerType(
  type: string | undefined,
  value: unknown
): string | undefined {
  if (isIoFileType(type)) return "File";
  // Declared type wins; only genuinely file-shaped OBJECT values in
  // unknown/other containers are promoted (never sniffed out of strings).
  if (type !== "str" && coerceIoFileLike(value)) return "File";
  return type;
}

export function inferIoDataType(
  type: string | undefined,
  value: unknown
): DataTypeValue {
  const normalized = normalizeIoContainerType(type, value);
  if (normalized === DataType.FILE) return DataType.FILE;
  if (typeof value === "number") return DataType.NUMBER;
  if (typeof value === "boolean") return DataType.BOOL;
  if (normalized && DATA_TYPE_VALUES.has(normalized)) {
    return normalized as DataTypeValue;
  }
  return DataType.STR;
}

export function unwrapSingleItemList(value: unknown, isList: boolean): unknown {
  if (isList || !Array.isArray(value) || value.length !== 1) return value;
  return value[0];
}

export function normalizePreviewIoContainer(
  entry: unknown,
  isList: boolean
): NormalizedIoContainer {
  const container = normalizeIoContainer(entry);
  const value = unwrapSingleItemList(container.value, isList);
  if (value === container.value) return container;
  const file = coerceIoFileLike(value);
  if (file) return { type: "File", value: file };
  return { ...container, value };
}
